import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Hooks für den PVE-Token-Setup-Flow (Sprint-3-Carry-Over, WT-307).
 *
 * Backend-Endpunkte (siehe docs/api/openapi.yaml, Erweiterung Sprint 3):
 *   GET    /secrets/pve                          -> Liste aller PVE-faehigen Systems
 *   POST   /secrets/pve/{system_id}              -> Token setzen / aktualisieren
 *   POST   /secrets/pve/{system_id}/test         -> Verbindungs-Test (PVE-Version)
 *   DELETE /secrets/pve/{system_id}              -> Token entfernen
 *   GET    /secrets/pve/{system_id}              -> existence-Probe (HEAD nicht
 *                                                    überall sauber via fetch)
 *
 * Backend-Lock: alle POST/DELETE setzen voraus, dass das Master-Passwort beim
 * Login entsperrt wurde - sonst 423 Locked oder 412 Precondition Failed.
 */

// --- DTOs ------------------------------------------------------------------

/**
 * Eintrag in der Secrets-Tabelle. Das Backend reichert die System-Row mit den
 * zur Anzeige benötigten Joins an (Customer-Name + Token-Existenz), damit die
 * UI nicht pro Zeile nach-fetchen muss.
 */
export interface SystemSecretRow {
  system_id: string;
  system_hostname: string;
  display_name?: string | null;
  customer_id: string;
  customer_name: string;
  service_type?: string | null;
  role: string;
  has_token: boolean;
  /** Optional, nur für Audit-Anzeige - kann fehlen. */
  token_set_at?: string | null;
}

export interface SystemSecretListResponse {
  data: SystemSecretRow[];
}

export interface SetSecretInput {
  systemId: string;
  token: string;
  description?: string;
}

export interface TestSecretInput {
  systemId: string;
  /** Optional: ungespeicherten Token testen (sonst gespeicherten verwenden). */
  token?: string;
}

export interface TestSecretResult {
  ok: boolean;
  /** PVE-Version laut /version oder null bei Fehler. */
  pve_version?: string | null;
  /** Gemessene Roundtrip-Zeit in ms. */
  latency_ms?: number | null;
  /** Bei !ok eine kurze Begründung. */
  error?: string | null;
}

export interface SystemSecretExistsResponse {
  has_token: boolean;
  token_set_at?: string | null;
}

// --- Query-Keys ------------------------------------------------------------

export const systemSecretsKeys = {
  all: ['system-secrets'] as const,
  list: ['system-secrets', 'list'] as const,
  exists: (systemId: string) => ['system-secrets', 'exists', systemId] as const,
};

// --- Queries ---------------------------------------------------------------

export function useSystemSecretsList(): ReturnType<
  typeof useQuery<SystemSecretListResponse>
> {
  return useQuery<SystemSecretListResponse>({
    queryKey: systemSecretsKeys.list,
    queryFn: () => api.get<SystemSecretListResponse>('/secrets/pve'),
    staleTime: 15_000,
  });
}

export function useSystemSecretExists(
  systemId: string | undefined
): ReturnType<typeof useQuery<SystemSecretExistsResponse>> {
  return useQuery<SystemSecretExistsResponse>({
    queryKey: systemSecretsKeys.exists(systemId ?? ''),
    queryFn: async () => {
      if (!systemId) return { has_token: false };
      try {
        // Realer Endpunkt ist /secrets/pve/{id}/exists mit {system_id, exists} —
        // der früher gerufene Pfad ohne /exists existierte nie, wodurch der
        // 404-Fallback IMMER griff und hinterlegte Tokens unsichtbar blieben.
        const raw = await api.get<{ system_id: string; exists: boolean }>(
          `/secrets/pve/${systemId}/exists`
        );
        return { has_token: raw.exists };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { has_token: false };
        }
        throw err;
      }
    },
    enabled: Boolean(systemId),
    staleTime: 15_000,
  });
}

// --- Mutations -------------------------------------------------------------

interface SetSecretBody {
  token: string;
  description?: string;
}

export function useSetSystemSecret(): ReturnType<
  typeof useMutation<void, Error, SetSecretInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, SetSecretInput>({
    mutationFn: ({ systemId, token, description }) => {
      const body: SetSecretBody = { token };
      if (description != null && description !== '') body.description = description;
      return api.post<void, SetSecretBody>(`/secrets/pve/${systemId}`, body);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: systemSecretsKeys.all });
      void qc.invalidateQueries({ queryKey: systemSecretsKeys.exists(vars.systemId) });
    },
  });
}

export function useDeleteSystemSecret(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (systemId) => api.delete<void>(`/secrets/pve/${systemId}`),
    onSuccess: (_data, systemId) => {
      void qc.invalidateQueries({ queryKey: systemSecretsKeys.all });
      void qc.invalidateQueries({ queryKey: systemSecretsKeys.exists(systemId) });
    },
  });
}

interface TestSecretBody {
  token?: string;
}

export function useTestSystemSecret(): ReturnType<
  typeof useMutation<TestSecretResult, Error, TestSecretInput>
> {
  return useMutation<TestSecretResult, Error, TestSecretInput>({
    mutationFn: ({ systemId, token }) => {
      const body: TestSecretBody = {};
      if (token != null && token !== '') body.token = token;
      return api.post<TestSecretResult, TestSecretBody>(
        `/secrets/pve/${systemId}/test`,
        body
      );
    },
  });
}
